import { Injectable, Injector, TemplateRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  SleipModalContent,
  SleipModalOverlayConfig,
} from '@shared/models/modal-config.model';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { SLEIP_MODAL_CONTENT } from '@shared/injection-tokens/injection-tokens';
import { SLEIP_MODAL_DEFAULT_CONFIG } from '@shared/config/sleip-modal-config';
import { ModalPositionType } from '@shared/types/modal-position.type';
import { ModalPositionEnum } from '@shared/enums/modal-position.enum';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(private injector: Injector, private overlay: Overlay) {}

  private overlayRef!: OverlayRef;

  private footerActionsTemplateRef: BehaviorSubject<TemplateRef<Element> | null> =
    new BehaviorSubject<TemplateRef<Element> | null>(null);
  footerActionsTemplate$ = this.footerActionsTemplateRef.asObservable();

  private modalData: BehaviorSubject<Record<string, unknown> | null> =
    new BehaviorSubject<Record<string, unknown> | null>(null);
  modalData$ = this.modalData.asObservable();

  private getOverlayConfig(config: SleipModalOverlayConfig): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      height: config.height,
      width: config.width,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.getOverlayPositionStratergy(
        config.position,
        config.positionOffset
      ),
    });
  }

  private createOverlay(config: SleipModalOverlayConfig): OverlayRef {
    const overlayConfig = this.getOverlayConfig(config);

    return this.overlay.create(overlayConfig);
  }

  private createInjector(modalContent?: SleipModalContent): Injector {
    const injector = Injector.create({
      providers: [{ provide: SLEIP_MODAL_CONTENT, useValue: modalContent }],
      parent: this.injector,
    });
    return injector;
  }

  open(config: SleipModalOverlayConfig) {
    const dialogConfig = { ...SLEIP_MODAL_DEFAULT_CONFIG, ...config };
    const overlay = this.createOverlay(dialogConfig);
    this.overlayRef = overlay;
    const injector = this.createInjector(config.data);
    const portal = new ComponentPortal(ModalComponent, null, injector);

    this.overlayRef.attach(portal);
    this.overlayRef.backdropClick().subscribe(() => {
      if (config.enableClickOutSideDismiss) {
        this.overlayRef.dispose();
      }
    });
  }

  close() {
    this.overlayRef?.dispose();
    this.footerActionsTemplateRef.next(null);
  }

  setModalFooterActions(footerActionsTemplateRef: TemplateRef<Element>) {
    this.footerActionsTemplateRef.next(footerActionsTemplateRef);
  }

  emitData(data: Record<string, unknown> | null) {
    this.modalData.next(data);
  }

  private getOverlayPositionStratergy(
    position: ModalPositionType = ModalPositionEnum.MIDDEL,
    offset?: string
  ) {
    switch (position) {
      case ModalPositionEnum.TOP:
        return this.overlay
          .position()
          .global()
          .centerHorizontally()
          .top(offset);
      case ModalPositionEnum.MIDDEL:
        return this.overlay
          .position()
          .global()
          .centerHorizontally()
          .centerVertically(offset);
      case ModalPositionEnum.BOTTOM:
        return this.overlay
          .position()
          .global()
          .centerHorizontally()
          .bottom(offset);
      default:
        return this.overlay
          .position()
          .global()
          .centerHorizontally()
          .centerVertically();
    }
  }
}
